import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  TablePagination,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { SortOrder, TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";

import { SearchBar } from "@/components/molecules/SearchBar";
import { StyledDataTable } from "@/components/molecules/StyledDataTable";
import { useSpecialistDropdown } from "@/hooks/useSpecialistDropDown";
import { DefaultAppPage } from "@/layouts";
import { Loader } from "@/pages/UserDetails/styles";
import { AddUserPopup } from "@/pages/Users/components/AddUserPopup";
import {
  HorizontalFlexContainer,
  HorizontalLeftAlignContainer,
  HorizontalRightAlignContainer,
  NoData,
  UserDisplayButton,
} from "@/styles/flex";

import { difference, USERS_TABLE_COLUMNS } from "./columns";
import { GET_DEACTIVATED_USERS, GET_FILTERED_USERS } from "./queries";
import { DataRow, FilteredResponse, Response } from "./types";

export const TestUserDisplay = ({
  onShowTestUsers,
  showTestUsers,
  disabled,
}: {
  onShowTestUsers: React.MouseEventHandler<HTMLButtonElement>;
  showTestUsers: boolean;
  disabled: boolean;
}) => {
  return (
    <UserDisplayButton>
      <Button disabled={disabled} onClick={onShowTestUsers} variant="outlined">
        {showTestUsers ? "Hide" : "Show"} Test Patients
      </Button>
    </UserDisplayButton>
  );
};

const CustomNoRowsOverlay = () => {
  return (
    <NoData>
      <Typography>Select a Moneta Therapist from the dropdown above to get started </Typography>
    </NoData>
  );
};

export const DeactivatedUserDisplay = ({
  onShowDeactivatedUsers,
  showDeactivatedUsers,
}: {
  onShowDeactivatedUsers: React.MouseEventHandler<HTMLButtonElement>;
  showDeactivatedUsers: boolean;
}) => {
  return (
    <UserDisplayButton>
      <Button onClick={onShowDeactivatedUsers} variant="outlined">
        {showDeactivatedUsers ? "Hide" : "Show"} Deactivated Patients
      </Button>
    </UserDisplayButton>
  );
};

type SortDetails = {
  column: string;
  order: "ASC" | "DESC";
};

export const Users = (): JSX.Element => {
  const encodedEmail = localStorage.getItem("username") || "";
  const currentUserEmail = atob(encodedEmail);

  const { options: specialistOptions, loading: loadingSpecialists } = useSpecialistDropdown();

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
    const stored = localStorage.getItem("rowsPerPage");
    return stored ? parseInt(stored, 10) : 20;
  });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [sorting, setSorting] = useState<SortDetails>({
    column: "fullName",
    order: "ASC",
  });
  const [accounts, setAccounts] = useState<DataRow[]>([]);

  const specialistNames = React.useMemo(() => {
    return specialistOptions.map((option) => option.firstName).sort((a, b) => a.localeCompare(b));
  }, [specialistOptions]);

  const specialistIds = React.useMemo(() => {
    return specialistOptions.reduce((acc, option) => {
      acc[option.firstName] = option.value;
      return acc;
    }, {} as Record<string, number>);
  }, [specialistOptions]);

  const [filterText, setFilterText] = useState<string>("");
  const [debouncedText, setDebouncedText] = useState<string>("");

  const [selectedSpecialist, setSelectedSpecialist] = useState<string>();

  useEffect(() => {
    if (specialistOptions.length > 0 && currentUserEmail) {
      const userSpecialist = specialistOptions.find(
        (specialist) => specialist.email === currentUserEmail
      );

      if (userSpecialist) {
        setSelectedSpecialist(userSpecialist.firstName);
      } else {
        setSelectedSpecialist("All");
      }
    }
  }, [specialistOptions, currentUserEmail]);

  const [selectedPrograms, setSelectedPrograms] = useState<string[]>(() => {
    const saved = localStorage.getItem("selectedPrograms");
    return saved ? JSON.parse(saved) : ["Intensive"];
  });
  const [resetPaginationToggle, setResetPaginationToggle] = useState<boolean>(false);
  const [showTestUsers, setShowTestUsers] = useState<boolean>(false);
  const [showDeactivatedUsers, setShowDeactivatedUsers] = useState<boolean>(false);
  const [buttonPopup, setButtonPopup] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showError, setShowError] = useState<boolean>(false);

  const navigate = useNavigate();

  // Calculate skip value for pagination
  const skip = (currentPage - 1) * rowsPerPage;

  // Get specialist IDs for the backend query
  const getSpecialistIdsForQuery = React.useCallback(() => {
    if (selectedSpecialist === "All") {
      return undefined;
    }
    return [specialistIds[selectedSpecialist || ""]];
  }, [selectedSpecialist, specialistIds]);

  // Get program types for the backend query
  const getProgramTypesForQuery = React.useCallback(() => {
    if (selectedPrograms.includes("All") || selectedPrograms.length === 0) {
      return undefined;
    }

    return selectedPrograms.map((program) => {
      switch (program) {
        case "Maintenance":
          return "maintenance";
        case "Intensive":
          return "intensive";
        case "Maintain-My-Brain":
          return "maintain-my-brain";
        case "Inactive":
          return "INACTIVE";
        default:
          return program.toLowerCase();
      }
    });
  }, [selectedPrograms]);

  // Determine if we should skip the query (no filters selected)
  const shouldSkipQuery = React.useMemo(() => {
    // Skip if showing deactivated users
    if (showDeactivatedUsers) {
      return true;
    }

    // Don't skip if there's a search term
    if (debouncedText) {
      return false;
    }

    // Skip if no specialist selected
    if (!selectedSpecialist) {
      return true;
    }

    // Don't skip if filters are properly selected
    return false;
  }, [showDeactivatedUsers, selectedSpecialist, debouncedText]);

  // Get user's timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Query for filtered users - this will now handle all cases
  const {
    data: FilteredData,
    loading: FilteredLoading,
    error: FilteredError,
    refetch: FilteredRefetch,
  } = useQuery<FilteredResponse>(GET_FILTERED_USERS, {
    variables: {
      skip,
      take: rowsPerPage,
      isTestUser: showTestUsers,
      specialistId: getSpecialistIdsForQuery(),
      ProgramType: getProgramTypesForQuery(),
      sortColumn: sorting.column,
      sortOrder: sorting.order,
      name: debouncedText || undefined,
    },
    fetchPolicy: "cache-and-network",
    skip: shouldSkipQuery,
    notifyOnNetworkStatusChange: true,
  });

  // Query for deactivated users
  const {
    data: DeactivatedData,
    loading: DeactivatedLoading,
    error: DeactivatedError,
    refetch: DeactivatedRefetch,
  } = useQuery<Response>(GET_DEACTIVATED_USERS, {
    variables: { skip, take: rowsPerPage },
    fetchPolicy: "cache-and-network",
    skip: !showDeactivatedUsers,
    notifyOnNetworkStatusChange: true,
  });

  // Update accounts state when data changes
  useEffect(() => {
    if (showDeactivatedUsers && DeactivatedData?.deactivatedAccounts) {
      setAccounts(DeactivatedData.deactivatedAccounts.deactivatedAccounts ?? []);
    } else if (!shouldSkipQuery && FilteredData?.accountsWithFilter?.accounts) {
      setAccounts(FilteredData.accountsWithFilter.accounts ?? []);
    } else {
      setAccounts([]);
    }
  }, [
    FilteredData?.accountsWithFilter.accounts,
    DeactivatedData?.deactivatedAccounts,
    showDeactivatedUsers,
    shouldSkipQuery,
  ]);

  // Update total items count from server response
  useEffect(() => {
    if (showDeactivatedUsers && DeactivatedData?.deactivatedAccounts) {
      setTotalItems(DeactivatedData.deactivatedAccounts.totalDeactivatedCount || 0);
    } else if (!shouldSkipQuery && FilteredData?.accountsWithFilter?.total) {
      setTotalItems(FilteredData.accountsWithFilter.total || 0);
    } else {
      setTotalItems(0);
    }
  }, [
    FilteredData?.accountsWithFilter?.total,
    DeactivatedData?.deactivatedAccounts,
    showDeactivatedUsers,
    shouldSkipQuery,
  ]);

  useEffect(() => {
    localStorage.setItem("tabValue", JSON.stringify(0));
  }, []);

  useEffect(() => {
    setIsLoading(
      (showDeactivatedUsers && DeactivatedLoading) ||
        loadingSpecialists ||
        (!shouldSkipQuery && FilteredLoading)
    );
  }, [
    showDeactivatedUsers,
    DeactivatedLoading,
    loadingSpecialists,
    FilteredLoading,
    shouldSkipQuery,
  ]);

  useEffect(() => {
    if ((showDeactivatedUsers && DeactivatedError) || FilteredError) {
      setShowError(true);
    }
  }, [showDeactivatedUsers, DeactivatedError, FilteredError]);

  useEffect(() => {
    localStorage.setItem("selectedPrograms", JSON.stringify(selectedPrograms));
  }, [selectedPrograms]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterText, selectedSpecialist, selectedPrograms, showTestUsers, showDeactivatedUsers]);

  const tableData: DataRow[] = accounts.map((account) => {
    const { firstName, lastName, latest_program_type } = account;

    return {
      ...account,
      fullName: `${firstName} ${lastName}`,
      programDuration: `${difference(account)}`,
      latest_program_type: `${latest_program_type}`,
    };
  });

  const handleClear = () => {
    if (filterText) {
      setResetPaginationToggle(!resetPaginationToggle);
      setFilterText("");
      setDebouncedText("");
      setCurrentPage(1);
    }
  };

  const handleTestToggleClick = () => {
    setShowTestUsers(!showTestUsers);
    setCurrentPage(1);
  };

  const handleDeactivatedToggleClick = () => {
    setShowDeactivatedUsers(!showDeactivatedUsers);
    setShowTestUsers(false);
    setCurrentPage(1);
  };

  const handleRowClicked = (row: DataRow) => {
    const userId = row.id;
    {
      showDeactivatedUsers
        ? navigate(`/patient-details/deactivated/${userId}`)
        : navigate(`/patient-details/${userId}`);
    }
  };

  const handleProgramsChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedPrograms(event.target.value as string[]);
    setCurrentPage(1);
  };

  const handleSpecialistChange = (event: SelectChangeEvent<string>) => {
    setSelectedSpecialist(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    const newPageNumber = newPage + 1;
    setCurrentPage(newPageNumber);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    localStorage.setItem("rowsPerPage", String(newRowsPerPage));
    setCurrentPage(1);
  };

  const handleChange = (value: string) => {
    setFilterText(value);
    if (!value) {
      setDebouncedText("");
    }
  };

  const handleSort = async (column: TableColumn<DataRow>, sortDirection: SortOrder) => {
    setSorting({
      column: column.sortField ?? "id",
      order: sortDirection === "asc" ? "ASC" : "DESC",
    });
  };

  // Debounce search text
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedText(filterText);
    }, 800);

    return () => {
      clearTimeout(handler);
    };
  }, [filterText]);

  // Refetch data when pagination or filters change
  useEffect(() => {
    const variables = {
      skip: (currentPage - 1) * rowsPerPage,
      take: rowsPerPage,
    };

    if (showDeactivatedUsers) {
      DeactivatedRefetch(variables);
    } else if (!shouldSkipQuery) {
      FilteredRefetch({
        ...variables,
        isTestUser: showTestUsers,
        specialistId: getSpecialistIdsForQuery(),
        ProgramType: getProgramTypesForQuery(),
        name: debouncedText || undefined,
        timezone: userTimezone,
      });
    }
  }, [
    currentPage,
    rowsPerPage,
    showTestUsers,
    showDeactivatedUsers,
    selectedSpecialist,
    selectedPrograms,
    debouncedText,
    shouldSkipQuery,
    getSpecialistIdsForQuery,
    getProgramTypesForQuery,
    DeactivatedRefetch,
    FilteredRefetch,
    userTimezone,
  ]);

  return (
    <DefaultAppPage>
      <>
        <HorizontalFlexContainer>
          <HorizontalLeftAlignContainer>
            <SearchBar onChange={handleChange} onClear={handleClear} searchText={filterText} />
            <FormControl
              variant="outlined"
              sx={{
                minWidth: 120,
                marginLeft: 2,
                maxWidth: 200,
              }}
            >
              <InputLabel id="specialist-select-label">MT</InputLabel>
              <Select
                label="MT"
                labelId="specialist-select-label"
                id="specialist-select"
                value={selectedSpecialist || ""}
                onChange={handleSpecialistChange}
              >
                <MenuItem value="All">
                  <em>All</em>
                </MenuItem>

                {specialistNames.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              variant="outlined"
              sx={{
                minWidth: 120,
                marginLeft: 2,
                maxWidth: 200,
              }}
            >
              <InputLabel id="program-select-label">Program</InputLabel>
              <Select
                labelId="program-select-label"
                label="Program"
                id="program-select"
                multiple
                value={selectedPrograms}
                onChange={handleProgramsChange}
                renderValue={(selected) => selected.join(", ")}
              >
                <MenuItem value="All">
                  <em>All</em>
                </MenuItem>
                <MenuItem value="Maintenance">Maintenance</MenuItem>
                <MenuItem value="Intensive">Intensive</MenuItem>
                <MenuItem value="Maintain-My-Brain">Maintain My Brain</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </HorizontalLeftAlignContainer>
          <HorizontalRightAlignContainer>
            <Button onClick={() => setButtonPopup(true)} variant="contained" className="button">
              Add Patient
            </Button>
          </HorizontalRightAlignContainer>
        </HorizontalFlexContainer>
        <br />

        {showTestUsers ? (
          <Typography variant="h6" sx={{ textDecoration: "underline" }}>
            Test Patients
          </Typography>
        ) : null}
        {showDeactivatedUsers ? (
          <Typography variant="h6" sx={{ textDecoration: "underline" }}>
            Deactivated Patients
          </Typography>
        ) : null}
        <br />

        <StyledDataTable
          columns={USERS_TABLE_COLUMNS}
          data={tableData}
          onRowClicked={handleRowClicked}
          pagination={false}
          defaultSortFieldId={2}
          noDataComponent={<CustomNoRowsOverlay />}
          progressPending={isLoading}
          onSort={handleSort}
          sortServer
          progressComponent={
            <Loader>
              <CircularProgress />
            </Loader>
          }
        />

        {/* Server-Side Pagination */}
        <Box sx={{ display: "flex", justifyContent: "end", mt: 2, mb: 2 }}>
          <TablePagination
            component="div"
            count={totalItems}
            page={currentPage - 1}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 50, 100]}
            showFirstButton
            showLastButton
          />
        </Box>

        <Grid container justifyContent="space-between" sx={{ mt: 2, mb: 2 }}>
          <TestUserDisplay
            onShowTestUsers={handleTestToggleClick}
            showTestUsers={showTestUsers}
            disabled={showDeactivatedUsers}
          />
          <DeactivatedUserDisplay
            onShowDeactivatedUsers={handleDeactivatedToggleClick}
            showDeactivatedUsers={showDeactivatedUsers}
          />
        </Grid>
        <AddUserPopup trigger={buttonPopup} setTrigger={setButtonPopup} />
        <br />
        <a href="/access-log">View access log</a>
      </>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Failed to fetch patient table data.
        </Alert>
      </Snackbar>
    </DefaultAppPage>
  );
};
